// Mudanças no cadastro da UC, com motivo (migration 20261029180000).
//
// Pedido do Felipe (29/10/2026): o Faturamento (Ítalo, Alice) pede ao Financeiro
// (João Henrique, Dhaiane, Bruna) a correção do cadastro, e o Financeiro registra o
// desconto negociado com o cliente. Quem calcula e emite o boleto vê o que mudou,
// quem mudou, quando e por quê — e não precisa adivinhar se vale o termo de adesão
// ou o sistema: vale o cadastro, e o motivo está ali.
//
// Duas fontes no banco, juntadas aqui:
//   uc_alteracoes_cadastro → cliente, titular, pagador, número, endereço, contatos
//   uc_desconto_historico  → desconto (já existia, desde 09/10/2026)
//
// O motivo vai junto do update, na coluna de passagem `motivo_alteracao` (o
// gatilho registra e limpa). As telas do Financeiro exigem o motivo; o banco não.
//
// Sem imports de runtime fora de src/lib: roda sob `node --test`.
import type { SupabaseClient } from '@supabase/supabase-js';
import { CAMPOS_CADASTRAIS_UC } from './permissaoCadastro.ts';
import { percentTexto, type RegistroDesconto } from './descontoHistorico.ts';

export const MOTIVO_MINIMO = 10;

/** Motivo com pelo menos 10 letras — "ok" ou "ajuste" não explicam nada a quem calcula. */
export const motivoValido = (motivo: string | null | undefined): boolean =>
    (motivo ?? '').trim().length >= MOTIVO_MINIMO;

/** Colunas da UC cuja mudança pede motivo (UC em operação). `endereco` é derivado. */
export const CAMPOS_COM_MOTIVO: readonly string[] = [
    ...CAMPOS_CADASTRAIS_UC.filter(c => c !== 'endereco'),
    'desconto_tarifa', 'desconto_bandeira',
];

export type GrupoCampo = 'cliente' | 'titular' | 'pagador' | 'desconto' | 'numero_uc' | 'endereco' | 'contatos';

const INFO_CAMPO: Record<string, { rotulo: string; grupo: GrupoCampo }> = {
    nome_cliente: { rotulo: 'Nome do cliente (assinante)', grupo: 'cliente' },
    cpf_cnpj_cliente: { rotulo: 'CPF/CNPJ do cliente', grupo: 'cliente' },
    data_nascimento_cliente: { rotulo: 'Nascimento do cliente', grupo: 'cliente' },
    titular_fatura: { rotulo: 'Titular da conta de luz', grupo: 'titular' },
    cpf_cnpj_titular: { rotulo: 'CPF/CNPJ do titular', grupo: 'titular' },
    data_nascimento_titular: { rotulo: 'Nascimento do titular', grupo: 'titular' },
    pagador_id: { rotulo: 'Pagador do boleto', grupo: 'pagador' },
    'pagador.nome': { rotulo: 'Nome do pagador', grupo: 'pagador' },
    'pagador.cpf_cnpj': { rotulo: 'CPF/CNPJ do pagador', grupo: 'pagador' },
    'pagador.email': { rotulo: 'E-mail do pagador', grupo: 'pagador' },
    'pagador.whatsapp': { rotulo: 'WhatsApp do pagador', grupo: 'pagador' },
    desconto_tarifa: { rotulo: 'Desconto na tarifa', grupo: 'desconto' },
    desconto_bandeira: { rotulo: 'Desconto na bandeira', grupo: 'desconto' },
    numero_uc: { rotulo: 'Número da UC', grupo: 'numero_uc' },
    outro_numero_uc: { rotulo: 'Outro nº da UC', grupo: 'numero_uc' },
    cep: { rotulo: 'CEP', grupo: 'endereco' },
    logradouro: { rotulo: 'Logradouro', grupo: 'endereco' },
    numero: { rotulo: 'Número', grupo: 'endereco' },
    complemento: { rotulo: 'Complemento', grupo: 'endereco' },
    quadra: { rotulo: 'Quadra', grupo: 'endereco' },
    lote: { rotulo: 'Lote', grupo: 'endereco' },
    bairro: { rotulo: 'Bairro', grupo: 'endereco' },
    cidade: { rotulo: 'Cidade', grupo: 'endereco' },
    estado: { rotulo: 'UF', grupo: 'endereco' },
    whatsapp_cliente: { rotulo: 'WhatsApp', grupo: 'contatos' },
    whatsapp_cliente_2: { rotulo: 'WhatsApp 2', grupo: 'contatos' },
    email_cliente: { rotulo: 'E-mail', grupo: 'contatos' },
    email_cliente_2: { rotulo: 'E-mail 2', grupo: 'contatos' },
    outro_email: { rotulo: 'Outro e-mail', grupo: 'contatos' },
};

export const rotuloCampo = (campo: string): string => INFO_CAMPO[campo]?.rotulo ?? campo;
export const grupoCampo = (campo: string): GrupoCampo => INFO_CAMPO[campo]?.grupo ?? 'contatos';

type Valor = string | number | boolean | null | undefined;

/** Igual ao `_cadastro_norm` do banco: só letras, números e @, sem diferença de caixa. */
export function normCadastro(v: Valor): string | null {
    const s = String(v ?? '').replace(/[^\p{L}\p{N}@]/gu, '').toUpperCase();
    return s || null;
}

/** Desconto em centésimos de ponto (0,01%). O PostgREST devolve `numeric` como texto. */
const descontoNorm = (v: Valor): number | null =>
    v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? null : Math.round(Number(v) * 10000);

const dataNorm = (v: Valor): string | null => (v ? String(v).slice(0, 10) : null);

function mesmoValor(campo: string, a: Valor, b: Valor): boolean {
    if (campo === 'desconto_tarifa' || campo === 'desconto_bandeira') return descontoNorm(a) === descontoNorm(b);
    if (campo.startsWith('data_')) return dataNorm(a) === dataNorm(b);
    if (campo === 'pagador_id') return (a || null) === (b || null);
    return normCadastro(a) === normCadastro(b);
}

export interface CampoAlterado {
    campo: string;
    antes: Valor;
    depois: Valor;
}

/**
 * O que muda, entre o cadastro atual e o que vai ser gravado, nos campos que pedem
 * motivo. Só olha as chaves que estão no `novo` — o que não vai no update não muda.
 */
export function camposAlterados(atual: Record<string, any> | null | undefined, novo: Record<string, any>): CampoAlterado[] {
    if (!atual) return [];
    return CAMPOS_COM_MOTIVO
        .filter(campo => Object.prototype.hasOwnProperty.call(novo, campo))
        .filter(campo => !mesmoValor(campo, atual[campo], novo[campo]))
        .map(campo => ({ campo, antes: atual[campo] ?? null, depois: novo[campo] ?? null }));
}

const formatarDoc = (texto: string): string => {
    const d = texto.replace(/\D/g, '');
    if (d.length === 11) return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    if (d.length === 14) return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    return texto;
};

/** Valor como a pessoa lê: CPF com máscara, data dd/mm/aaaa, desconto em %. */
export function formatarValorCampo(campo: string, valor: Valor): string {
    if (valor === null || valor === undefined || String(valor).trim() === '') return '(vazio)';
    const s = String(valor).trim();
    if (campo === 'desconto_tarifa' || campo === 'desconto_bandeira') return percentTexto(valor as any);
    if (campo.startsWith('data_')) {
        const [a, m, d] = s.slice(0, 10).split('-');
        return a && m && d ? `${d}/${m}/${a}` : s;
    }
    if (campo.includes('cpf_cnpj')) return formatarDoc(s);
    // Pagador: o banco grava "NOME · documento".
    if (campo === 'pagador_id') {
        const [nome, doc] = s.split(' · ');
        return doc ? `${nome} (${formatarDoc(doc)})` : nome;
    }
    return s;
}

export const descreverCampo = (c: CampoAlterado): string =>
    `${rotuloCampo(c.campo)}: ${formatarValorCampo(c.campo, c.antes)} → ${formatarValorCampo(c.campo, c.depois)}`;

// ── Registros juntados das duas fontes ───────────────────────────────────────

export interface AlteracaoCadastro {
    /** Único entre as duas fontes ("c-12", "d-34"). */
    chave: string;
    alteradoEm: string;
    alteradoPor: string | null;
    motivo: string | null;
    /** Mudança sem pessoa por trás: robô, cron, cascata de gatilho. */
    automatica: boolean;
    /** De onde veio, quando ajuda a entender (ex.: "no cálculo da fatura"). */
    onde: string | null;
    campos: CampoAlterado[];
    /** Desconto que vale só numa fatura (o cadastro não mudou). */
    soNaFatura: boolean;
    faturaId: number | null;
    /** Mudança no cadastro do pagador, que vale para todas as UCs dele. */
    doPagador: boolean;
}

export interface RegistroCadastroDb {
    id: number;
    uc_id: number | null;
    pagador_id: string | null;
    campos: CampoAlterado[] | null;
    motivo: string | null;
    origem: string | null;
    alterado_por_nome: string | null;
    alterado_em: string;
}

export function deRegistroCadastro(r: RegistroCadastroDb): AlteracaoCadastro {
    const doPagador = !!r.pagador_id;
    return {
        chave: `c-${r.id}`,
        alteradoEm: r.alterado_em,
        alteradoPor: r.alterado_por_nome,
        motivo: r.motivo?.trim() || null,
        automatica: r.origem === 'sistema',
        onde: doPagador ? 'no cadastro do pagador (vale para todas as UCs dele)' : null,
        campos: Array.isArray(r.campos) ? r.campos : [],
        soNaFatura: false,
        faturaId: null,
        doPagador,
    };
}

const ONDE_DESCONTO: Record<string, string> = {
    calculo_fatura: 'no cálculo da fatura',
    cadastro_uc: 'no cadastro da UC',
};

export function deRegistroDesconto(r: RegistroDesconto): AlteracaoCadastro {
    const campos: CampoAlterado[] = [];
    const tarifaMudou = descontoNorm(r.desconto_tarifa_antes) !== descontoNorm(r.desconto_tarifa_depois);
    const bandeiraMudou = descontoNorm(r.desconto_bandeira_antes) !== descontoNorm(r.desconto_bandeira_depois);
    if (tarifaMudou || !bandeiraMudou) campos.push({ campo: 'desconto_tarifa', antes: r.desconto_tarifa_antes, depois: r.desconto_tarifa_depois });
    if (bandeiraMudou) campos.push({ campo: 'desconto_bandeira', antes: r.desconto_bandeira_antes, depois: r.desconto_bandeira_depois });
    const semPessoa = !r.alterado_por_nome || r.alterado_por_nome === 'Sistema';
    return {
        chave: `d-${r.id}`,
        alteradoEm: r.alterado_em,
        alteradoPor: semPessoa ? null : r.alterado_por_nome,
        motivo: r.motivo?.trim() || null,
        automatica: semPessoa,
        onde: r.escopo === 'fatura'
            ? (r.fatura?.mes_referencia ? `só na fatura de ${r.fatura.mes_referencia}` : 'só numa fatura')
            : (r.origem ? ONDE_DESCONTO[r.origem] ?? null : null),
        campos,
        soNaFatura: r.escopo === 'fatura',
        faturaId: r.fatura_id ?? null,
        doPagador: false,
    };
}

/** Desconto "cadastro inicial" (sem antes) não é mudança: é a UC nascendo. */
const ehCadastroInicial = (r: RegistroDesconto) =>
    r.escopo === 'uc' && descontoNorm(r.desconto_tarifa_antes) === null && descontoNorm(r.desconto_bandeira_antes) === null;

/**
 * Junta as duas fontes, mais recente primeiro. Desconto "só numa fatura" entra
 * apenas quando é da fatura aberta na tela (`faturaId`); sem fatura (histórico da
 * UC), entram todos.
 */
export function juntarAlteracoes(
    cadastro: RegistroCadastroDb[],
    desconto: RegistroDesconto[],
    faturaId?: number | string | null,
): AlteracaoCadastro[] {
    const descontos = desconto
        .filter(r => !ehCadastroInicial(r))
        .filter(r => r.escopo === 'uc' || faturaId == null || String(r.fatura_id) === String(faturaId))
        .map(deRegistroDesconto);
    return [...cadastro.map(deRegistroCadastro), ...descontos]
        .filter(a => a.campos.length > 0)
        .sort((a, b) => b.alteradoEm.localeCompare(a.alteradoEm));
}

/**
 * O que explica o cadastro de hoje: para cada grupo (cliente, titular, pagador,
 * desconto...), a mudança mais recente. Uma mudança que já foi desfeita ou
 * substituída por outra mais nova sai da lista curta. Desconto só desta fatura
 * sempre fica.
 */
export function alteracoesVigentes(lista: AlteracaoCadastro[]): AlteracaoCadastro[] {
    const vistos = new Set<string>();
    const ordenada = [...lista].sort((a, b) => b.alteradoEm.localeCompare(a.alteradoEm));
    return ordenada.filter(a => {
        if (a.soNaFatura) return true;
        const grupos = [...new Set(a.campos.map(c => grupoCampo(c.campo)))];
        const novos = grupos.filter(g => !vistos.has(g));
        grupos.forEach(g => vistos.add(g));
        return novos.length > 0;
    });
}

/**
 * Mudança que o Faturamento ainda não viu num boleto: depois do último boleto
 * emitido desta UC. Sem boleto anterior (primeira cobrança), tudo é novidade.
 */
export function ehNovidade(a: AlteracaoCadastro, ultimoBoletoEm: string | null | undefined): boolean {
    if (!ultimoBoletoEm) return true;
    return new Date(a.alteradoEm).getTime() > new Date(ultimoBoletoEm).getTime();
}

export const textoSemMotivo = (a: AlteracaoCadastro): string =>
    a.automatica ? 'Mudança automática do sistema' : 'Sem motivo informado';

// ── Gravação ─────────────────────────────────────────────────────────────────

/** O update com o motivo junto (a coluna não guarda nada: o gatilho registra e limpa). */
export function comMotivo<T extends Record<string, unknown>>(payload: T, motivo: string | null | undefined): T & { motivo_alteracao?: string } {
    const m = (motivo ?? '').trim();
    return m ? { ...payload, motivo_alteracao: m } : payload;
}

/**
 * O banco ainda sem a migration 20261029180000: a coluna `motivo_alteracao` não
 * existe e o PostgREST recusa o update inteiro. A tela tenta de novo sem o motivo
 * para não travar o cadastro.
 */
export function erroColunaMotivoAusente(erro: unknown): boolean {
    const e = erro as { code?: string; message?: string } | null | undefined;
    return !!e && (e.code === 'PGRST204' || e.code === '42703') && /motivo_alteracao/.test(e.message || '');
}

// ── Leitura ──────────────────────────────────────────────────────────────────

export async function buscarAlteracoesCadastro(
    supabase: SupabaseClient,
    ucId: number | string,
    limite = 50,
): Promise<RegistroCadastroDb[]> {
    const { data, error } = await supabase
        .from('uc_alteracoes_cadastro')
        .select('id, uc_id, pagador_id, campos, motivo, origem, alterado_por_nome, alterado_em')
        .eq('uc_id', ucId)
        .order('alterado_em', { ascending: false })
        .limit(limite);
    if (error) throw new Error(error.message);
    return (data ?? []) as RegistroCadastroDb[];
}
